import { useAuth } from "wasp/client/auth";
import {
  useQuery,
  getPendingAccessRequests,
  approveAccessByPlatform,
  rejectAccessRequest,
} from "wasp/client/operations";
import DefaultLayout from "../../admin/layout/DefaultLayout";
import LoadingSpinner from "../../admin/layout/LoadingSpinner";
import { toast } from "../../hooks/use-toast";

export default function AdminAccessRequestsPage() {
  const { data: user } = useAuth();
  const { data, isLoading, refetch } = useQuery(getPendingAccessRequests, {
    scope: "platform",
  });
  const requests = (data as any[]) || [];

  if (!user?.isAdmin && !user?.isModerator) {
    return <div className="p-8">Access denied</div>;
  }

  return (
    <DefaultLayout user={user}>
      <div className="mx-auto max-w-4xl px-4 py-8">
        <h1 className="text-2xl font-bold">Access requests</h1>
        <p className="mt-1 text-sm text-slate-600">
          Platform approval grants Community &amp; Business only. Building
          managers unlock service requests separately.
        </p>

        {isLoading && <LoadingSpinner />}

        <div className="mt-6 space-y-3">
          {requests.map((r) => (
            <article
              key={r.id}
              className="rounded-2xl border border-slate-200 bg-white p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-semibold">
                    {r.user?.fullName || r.user?.username || r.user?.email}
                  </h2>
                  <p className="text-sm text-slate-500">{r.user?.email}</p>
                  <p className="mt-1 text-sm">
                    Building:{" "}
                    <span className="font-medium">
                      {r.location?.name} ({r.location?.locationCode})
                    </span>
                  </p>
                  {r.unitNumber && (
                    <p className="text-sm text-slate-600">Unit {r.unitNumber}</p>
                  )}
                  {r.message && (
                    <p className="mt-2 text-sm text-slate-700">{r.message}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rounded-xl bg-sky-700 px-3 py-2 text-sm font-semibold text-white"
                    onClick={async () => {
                      try {
                        await approveAccessByPlatform({ requestId: r.id });
                        toast({
                          title: "Approved",
                          description: "Community & business access granted",
                        });
                        await refetch();
                      } catch (e: any) {
                        toast({
                          title: "Error",
                          description: e?.message,
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
                    onClick={async () => {
                      try {
                        await rejectAccessRequest({ requestId: r.id });
                        await refetch();
                      } catch (e: any) {
                        toast({
                          title: "Error",
                          description: e?.message,
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            </article>
          ))}
          {!isLoading && requests.length === 0 && (
            <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-slate-500">
              No pending access requests.
            </p>
          )}
        </div>
      </div>
    </DefaultLayout>
  );
}
